import { CrashDescription, IssueTag } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { localStorageGet, LocalStorageKey } from '../data/client_local_storage.ts';

export function reportCrash(event: ErrorEvent) {
  // avoid reporting twice for same error
  // "reported" is a made up property - this works because the same error object is used the second time around
  if (event.error.reported) {
    return false;
  }
  event.error.reported = true;
  const details =
    event.target instanceof HTMLElement
      ? `Resource failed: ${event.target.tagName}, ${event.target.localName}, ${event.target.id}, ${event.target.className}`
      : '';
  const crashDescription = `${event.message}\n${event.filename}:${event.lineno}:${event.colno}\n${details}\n${event.error.stack}`;
  void reportCrashDescription(crashDescription);
  return false;
}

// Files a crash issue (and so a crash email, unless the server knows the description as a handled
// condition) for a problem the app caught rather than an uncaught error. Resolves once the issue has
// been sent, or failed to send - never rejects - so a caller about to reload the page can wait for it.
export function reportCrashDescription(crashDescription: string): Promise<void> {
  console.error(`Reporting error ${crashDescription}`);
  const bundleHash = localStorageGet(LocalStorageKey.BundleHash);
  const description: CrashDescription = {
    tag: IssueTag.Crash,
    description: crashDescription,
    bundleHash,
  };
  return server_api
    .addIssue({
      timestamp: Date.now(),
      description,
    })
    .catch((e) => console.log(`Failed to report the error: ${e?.message ?? e}`));
}

// Files a crash report for an error a render threw and React recovered from by rendering again
// synchronously (GitHub issue #4088). React's default handler raises it as a window error whose stack
// is only React's own frames, which is how two "Minified React error #520" reports told us nothing.
// What matters is the error's cause, which for #520 is the original error, and the component stack:
// the component that was rendering when the update that tripped React's loop detection was enqueued.
export function reportRecoverableError(error: unknown, errorInfo: { componentStack?: string | undefined }) {
  void reportCrashDescription(
    `React recoverable error (GitHub issue #4088)\n${describeError(error)}\nComponent stack:${errorInfo.componentStack ?? ' unknown'}`,
  );
}

// The error's stack followed by the stack of each error in its cause chain. WebKit's stacks do not
// start with the message line the way V8's do, so the message is added when it is missing.
export function describeError(error: unknown): string {
  const describeOne = (e: Error) =>
    e.stack?.includes(e.message) ? e.stack : `${e.name}: ${e.message}\n${e.stack ?? ''}`;
  if (!(error instanceof Error)) {
    return String(error);
  }
  let description = describeOne(error);
  let cause: unknown = error.cause;
  for (let depth = 0; cause !== undefined && depth < 5; depth++) {
    description += `\nCaused by: ${cause instanceof Error ? describeOne(cause) : String(cause)}`;
    cause = cause instanceof Error ? cause.cause : undefined;
  }
  return description;
}
